import { useRef } from 'react';
import { type CalendarDate, getLocalTimeZone, today } from '@internationalized/date';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { type DeliveryFilterValues, rangeToTimestamps } from '@/features/deliveries';
import type { DeliveryExportInput, DeliveryFilterInput } from '@/services/db/delivery/utils';

/**
 * URL-backed Outbox filter state. Keys mirror the Ember query params, and the
 * parsed shape matches `DeliveryFilterValues` (so the page forwards it straight
 * to `<DeliveryFilters>`): `actionType` defaults to `''` (all channels); the
 * rest are `null`/absent when unset. The `satisfies` keeps this map exhaustive
 * against `DeliveryFilterValues` — adding a filter field there fails here until a
 * parser is added, so a new URL param can't be silently dropped.
 */
const OUTBOX_FILTER_PARSERS = {
  actionType: parseAsString.withDefault(''),
  campaign: parseAsString,
  action: parseAsString,
  recipient: parseAsString,
  deliveryStatus: parseAsString,
  startTs: parseAsInteger,
  endTs: parseAsInteger,
} satisfies Record<keyof DeliveryFilterValues, unknown>;

export function useOutboxFilters(): {
  values: DeliveryFilterValues;
  onChange: (patch: Partial<DeliveryFilterValues>) => void;
} {
  const [values, setValues] = useQueryStates(OUTBOX_FILTER_PARSERS);
  return { values, onChange: (patch) => void setValues(patch) };
}

/** The number of days in the Outbox's default date window. */
const DEFAULT_RANGE_DAYS = 30;

/**
 * The Outbox's default filter window — the last 30 days, as **local-timezone** bounds
 * (Ember `base-deliveries` `LAST_30_DAYS`): local start-of-day 29 days ago through local
 * end-of-day today. Built by the same `rangeToTimestamps` the picker uses, so the seeded
 * default equals selecting the "Last 30 days" preset. The end is local end-of-*today* (a
 * future bound), so the 15s "load new" check keeps finding same-day sends and viewers west of
 * UTC aren't truncated. `todayDate`/`timeZone` are injectable for tests.
 */
export function defaultDeliveriesDateRange(
  todayDate: CalendarDate = today(getLocalTimeZone()),
  timeZone: string = getLocalTimeZone(),
): { startTs: number; endTs: number } {
  const start = todayDate.subtract({ days: DEFAULT_RANGE_DAYS - 1 });
  return rangeToTimestamps({ start, end: todayDate }, { timeZone });
}

/**
 * Apply the default 30-day window when no range is set, matching Ember's default-on-entry
 * (`base-deliveries` `setupController`). The default is derived purely during render — no effect,
 * no URL write — so the picker shows the window and the first query is already bounded, with no
 * flash. It applies only when **both** bounds are absent, so a partial or full range in the URL is
 * respected as-is; clearing the range (both absent again) simply falls back to the default. A
 * user-selected range is still persisted to the URL by the filter bar's own `onChange`.
 */
export function useSeedDefaultDateRange(values: DeliveryFilterValues): DeliveryFilterValues {
  // Compute the window once (stable identity across renders); its value is fixed for the day.
  const seededRangeRef = useRef<{ startTs: number; endTs: number } | undefined>(undefined);
  const seededRange = (seededRangeRef.current ??= defaultDeliveriesDateRange());

  const bothAbsent = values.startTs == null && values.endTs == null;
  return bothAbsent ? { ...values, ...seededRange } : values;
}

/** Whether any filter is active — drives the table's filter-aware empty state. */
export function hasActiveFilters(v: DeliveryFilterValues): boolean {
  return (
    v.actionType !== '' ||
    v.campaign != null ||
    v.action != null ||
    v.recipient != null ||
    v.deliveryStatus != null ||
    v.startTs != null ||
    v.endTs != null
  );
}

/** Filter values → the data layer's list-query input (`null` ⇒ omitted). */
export function toFilterInput(v: DeliveryFilterValues): DeliveryFilterInput {
  return {
    actionType: v.actionType,
    campaign: v.campaign ?? undefined,
    action: v.action ?? undefined,
    recipient: v.recipient ?? undefined,
    deliveryStatus: v.deliveryStatus ?? undefined,
    startTs: v.startTs ?? undefined,
    endTs: v.endTs ?? undefined,
  };
}

/** Format an epoch-second timestamp as `MMM DD, YYYY` in the viewer's local zone (matching the picker). */
function formatRangeDate(epochSeconds: number): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: '2-digit', year: 'numeric' }).format(
    new Date(epochSeconds * 1000),
  );
}

/**
 * The filter-aware empty-state sentence (Ember `cio-deliveries` `emptyMessage`):
 * "There are no <noun> [matching your filters] [for the selected time range (…)]".
 * The `noun` is passed in (this module can't call the taxonomy hook) — the
 * deliveries/sent noun, or "drafts" on the drafts tab.
 */
export function buildEmptyMessage(v: DeliveryFilterValues, drafted: boolean, deliveriesNoun: string): string {
  const parts = ['There are no', drafted ? 'drafts' : deliveriesNoun];
  const hasQueryFilters =
    v.actionType !== '' || v.campaign != null || v.action != null || v.recipient != null || v.deliveryStatus != null;
  if (hasQueryFilters) {
    parts.push('matching your filters');
  }
  if (v.startTs != null && v.endTs != null) {
    parts.push(`for the selected time range (${formatRangeDate(v.startTs)}–${formatRangeDate(v.endTs)})`);
  }
  return `${parts.join(' ')}.`;
}

/** Filter values → the CSV export input (the export modal adds its own date range). */
export function toExportInput(v: DeliveryFilterValues): DeliveryExportInput {
  return {
    actionType: v.actionType,
    deliveryStatus: v.deliveryStatus ?? undefined,
    campaign: v.campaign ?? undefined,
    action: v.action ?? undefined,
    startTs: v.startTs ?? undefined,
    endTs: v.endTs ?? undefined,
  };
}
